import { Avatar as AvatarComp, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useAvatarQuery } from "../api/avatar-hooks";
import AvatarUpload from "./avatar-upload";

export interface AvatarProps {
  avatarPath: string | null;
  size: number;
  storageName: string;
  fallbackInitials: string;
  editable?: boolean;
  uploadLabel?: string;
  deleteText?: string;
  onUpload?: (url: string) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  className?: string;
  statusDot?: "active" | "inactive";
}

export default function Avatar({
  avatarPath,
  size,
  storageName,
  fallbackInitials,
  editable = false,
  uploadLabel = "Select new avatar",
  deleteText = "Delete avatar",
  onUpload,
  onDelete,
  className,
  statusDot,
}: AvatarProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Use TanStack Query for avatar loading
  const {
    data: avatarUrl,
    isLoading: avatarLoading,
    error: avatarError,
  } = useAvatarQuery(storageName, avatarPath);

  const handleUpload = async (filePath: string) => {
    if (onUpload) {
      await onUpload(filePath);
    }
    setPopoverOpen(false);
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete();
    }
    setPopoverOpen(false);
  };

  const showLoading = !!avatarPath && avatarLoading;
  const avatarKey = avatarPath ?? "fallback";
  const dotSize = Math.max(8, Math.round(size * 0.3));

  const avatarContent = (
    <div className="relative inline-flex">
      <AvatarComp
        key={avatarKey}
        className={cn(
          "bg-muted",
          editable && "cursor-pointer transition-colors hover:opacity-90",
          className,
        )}
        title={showLoading ? "Loading avatar..." : avatarError ? "Error loading avatar" : undefined}
        style={{ height: size, width: size }}
      >
        {!!avatarUrl && !avatarError && (
          <AvatarImage src={avatarUrl} alt="Avatar" className="object-cover" />
        )}
        <AvatarFallback
          className={cn("bg-muted", showLoading && "animate-pulse", avatarError && "text-red-600")}
          style={{ fontSize: size * 0.4 }}
        >
          {fallbackInitials}
        </AvatarFallback>
      </AvatarComp>
      {statusDot ? (
        <span
          className={cn(
            "border-background absolute right-0 bottom-0 rounded-full border-2",
            statusDot === "active" ? "bg-green-500" : "bg-muted-foreground",
          )}
          style={{ width: dotSize, height: dotSize }}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  if (!editable || !onUpload) {
    return avatarContent;
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>{avatarContent}</PopoverTrigger>
      <PopoverContent className="w-80">
        <AvatarUpload
          storageName={storageName}
          uploadLabel={uploadLabel}
          deleteText={deleteText}
          onUpload={handleUpload}
          onDelete={onDelete ? handleDelete : undefined}
          onClose={() => setPopoverOpen(false)}
          hasAvatar={!!avatarUrl}
        />
      </PopoverContent>
    </Popover>
  );
}
